import { useCallback, useEffect, useMemo } from 'react';
import {
    ADT3DSceneAdapter,
    ADT3DScenePage,
    ConsumerDeeplinkContext,
    IADTInstance,
    Locale,
    TutorialModal,
    TelemetryService,
    Telemetry,
    TelemetryTypes
} from '@microsoft/iot-cardboard-js';
import appInsightsInstance from '../../Services/AppInsightsService';
import { useThemeContext } from '../../Theming/ThemeProvider';
import { useLanguageContext } from '../../Models/Context/I18nProvider';
import MsalAuthService from '../../Services/MsalAuthService';
import { useContext } from 'react';
import { AppContext } from '../App/App';
import {
    convertSeverityToAppInsightsSeverity,
    getSecureUrlObjFromString,
    getStorageContainerUrlFromLocalStorage,
    getTenantIdFromLocalStorage
} from '../../Services/Utils';
import classNames from 'classnames/bind';
import {
    AdtBlobContainerURLsStorageKey,
    AdtBlobContainerURLStorageKey,
    ConfigurationStorageKey,
    EnvironmentsStorageKey,
    LOCAL_STORAGE_KEYS,
    ScenesFreHasBeenSeenKey
} from '../../Models/Constants/Constants';
import styles from './Scene3D.module.scss';
const cx = classNames.bind(styles);

const Scene3D = () => {
    const { selectedTheme } = useThemeContext();
    const { selectedLang } = useLanguageContext();
    const {
        selectedAdtUrl,
        setSelectedAdtUrl,
        currentUserUniqueObjectId,
        is3DScenesSplashVisible,
        setIs3DScenesSplashVisible
    } = useContext(AppContext);

    const adapter = useMemo(
        () =>
            new ADT3DSceneAdapter(
                {
                    login: () => {},
                    getToken: (
                        tokenFor?:
                            | 'azureManagement'
                            | 'adx'
                            | 'storage'
                            | 'powerBI'
                    ) => {
                        return tokenFor === 'azureManagement'
                            ? MsalAuthService.getManagementToken()
                            : tokenFor === 'adx'
                            ? MsalAuthService.getAdxToken()
                            : tokenFor === 'storage'
                            ? MsalAuthService.getStorageToken()
                            : tokenFor === 'powerBI'
                            ? MsalAuthService.getPowerBIToken()
                            : MsalAuthService.getAdtToken();
                    }
                },
                getSecureUrlObjFromString(selectedAdtUrl)?.hostname || '',
                getStorageContainerUrlFromLocalStorage(),
                getTenantIdFromLocalStorage(),
                currentUserUniqueObjectId,
                undefined,
                undefined,
                false
            ),
        [selectedAdtUrl, currentUserUniqueObjectId]
    );

    const telemetryCallback = useCallback(
        async (telemetry: Telemetry.TelemetryItem) => {
            if (
                localStorage.getItem(
                    LOCAL_STORAGE_KEYS.FeatureFlags.Telemetry.debugLogging
                ) === 'true'
            ) {
                console.debug(
                    `[3DScenes] [Telemetry] ${telemetry.name} ${telemetry.type} emitted`,
                    telemetry
                );
            }
            // Add 3D Scenes as source for every telemetry log
            telemetry.customProperties['AppName'] = '3DScenes';

            switch (telemetry.type) {
                case TelemetryTypes.TelemetryType.metric:
                    appInsightsInstance.appInsights.trackMetric(
                        {
                            name: telemetry.name,
                            sampleCount: telemetry.sampleSize,
                            average: telemetry.average,
                            min: telemetry.min,
                            max: telemetry.max
                        },
                        telemetry.customProperties
                    );
                    break;
                case TelemetryTypes.TelemetryType.event:
                    appInsightsInstance.appInsights.trackEvent(
                        { name: telemetry.name },
                        telemetry.customProperties
                    );
                    break;
                case TelemetryTypes.TelemetryType.request:
                    break;
                case TelemetryTypes.TelemetryType.exception:
                    appInsightsInstance.appInsights.trackException(
                        {
                            exception: {
                                name: telemetry.name,
                                message: telemetry.message || '',
                                stack: telemetry.stack,
                                columnNumber: -1,
                                error: {},
                                lineNumber: -1,
                                url: ''
                            },
                            id: telemetry.exceptionId,
                            severityLevel: convertSeverityToAppInsightsSeverity(
                                telemetry.severityLevel
                            )
                        },
                        telemetry.customProperties
                    );
                    break;
                case TelemetryTypes.TelemetryType.trace:
                    appInsightsInstance.appInsights.trackTrace(
                        {
                            message: telemetry.message,
                            severityLevel: convertSeverityToAppInsightsSeverity(
                                telemetry.severityLevel
                            )
                        },
                        telemetry.customProperties
                    );
                    break;
                default:
                    console.warn(
                        `Received unexpected telemetry event of type ${
                            (telemetry as any).type
                        }. Ignoring.`
                    );
                    break;
            }
        },
        []
    );
    useEffect(() => {
        TelemetryService.registerTelemetryCallback(telemetryCallback);
    }, [telemetryCallback]);

    const handleAdtInstanceChange = (
        adtInstance: string | IADTInstance,
        _adtInstances: Array<string | IADTInstance>
    ) => {
        if (adtInstance) {
            setSelectedAdtUrl(
                typeof adtInstance === 'string'
                    ? adtInstance
                    : 'https://' + adtInstance.properties.hostName
            );
        }
    };

    const onLinkGenerated = (link: string) => {
        return `${link}&tid=${getTenantIdFromLocalStorage()}`;
    };

    return (
        <div className={cx('scene3DWrapper')}>
            {is3DScenesSplashVisible && (
                <div className={cx('tutorialWrapper')}>
                    <TutorialModal
                        theme={selectedTheme}
                        locale={selectedLang as Locale}
                        isOpen={is3DScenesSplashVisible}
                        defaultPageKey={
                            !!localStorage.getItem(ScenesFreHasBeenSeenKey)
                                ? ('CONCEPTS' as any)
                                : ('INTRODUCTION' as any)
                        }
                        onDismiss={() => {
                            localStorage.setItem(
                                ScenesFreHasBeenSeenKey,
                                'true'
                            );
                            setIs3DScenesSplashVisible(false);
                        }}
                    />
                </div>
            )}
            <ConsumerDeeplinkContext onGenerateDeeplink={onLinkGenerated}>
                <ADT3DScenePage
                    theme={selectedTheme}
                    locale={selectedLang as Locale}
                    adapter={adapter}
                    environmentPickerOptions={{
                        adt: {
                            isLocalStorageEnabled: true,
                            localStorageKey: EnvironmentsStorageKey,
                            selectedItemLocalStorageKey:
                                ConfigurationStorageKey,
                            onAdtInstanceChange: handleAdtInstanceChange
                        },
                        storage: {
                            isLocalStorageEnabled: true,
                            localStorageKey: AdtBlobContainerURLsStorageKey,
                            selectedItemLocalStorageKey:
                                AdtBlobContainerURLStorageKey,
                            onContainerChange: () => {
                                return;
                            }
                        }
                    }}
                />
            </ConsumerDeeplinkContext>
        </div>
    );
};

export default Scene3D;
